import {EnvContext} from "../App";
import LoginService from "../services/LoginService";
import React, {useContext, useState, useEffect} from "react";
import EnvProvider from "../api/EnvProvider";


interface AuthContextType {
    authenticated: boolean;
    isImpersonating: boolean;
    impersonatedUser: string | null;
    login: (user: string, successCallback: VoidFunction, errorCallback: (any:any) => {}) => void;
    logout: (callback: VoidFunction) => void;
    startImpersonation: (impersonationToken: string, targetEmail: string) => void;
    stopImpersonation: () => void;
    userType: string | null;

}

export const  AuthContext = React.createContext<AuthContextType>(null!);

export default function AuthProvider({children}: { children: React.ReactNode }) {
    const envProvider: EnvProvider = useContext(EnvContext) as EnvProvider;
    const loginService = new LoginService(envProvider)
    let [authenticated, setAuthenticated] = useState(false);
    let [isImpersonating, setIsImpersonating] = useState(false);
    let [impersonatedUser, setImpersonatedUser] = useState<string | null>(null);
    let [userType, setUserType] = useState<string | null>(null);




    // Helper function to check if JWT is expired
    const isTokenExpired = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp * 1000; // Convert to milliseconds
            return Date.now() >= exp;
        } catch (error) {
            console.warn('Failed to parse JWT token:', error);
            return true; // Treat unparseable tokens as expired
        }
    };

    // Helper function to check if Cognito token needs refresh (within 5 minutes of expiry)
    const shouldRefreshCognitoToken = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp * 1000;
            const fiveMinutes = 5 * 60 * 1000;
            return (exp - Date.now()) < fiveMinutes;
        } catch (error) {
            return false;
        }
    };

    // Function to refresh Cognito token
    const refreshCognitoTokenInProvider = async (): Promise<boolean> => {
        try {
            const { fetchAuthSession } = await import('@aws-amplify/auth');
            const session = await fetchAuthSession({ forceRefresh: true });
            const newIdToken = session.tokens?.idToken?.toString();
            
            if (newIdToken) {
                localStorage.setItem('cognito_id_token', newIdToken);
                //console.log('AuthProvider: Cognito token refreshed successfully');
                return true;
            }
        } catch (error) {
            console.warn('AuthProvider: Failed to refresh Cognito token:', error);
            // Don't redirect here - let the Api.tsx interceptor handle it
        }
        return false;
    };

    // Initialize impersonation state on mount and check for expired tokens
    useEffect(() => {
        const impersonationToken = localStorage.getItem('impersonation_token');
        const impersonatedUserEmail = localStorage.getItem('impersonated_user');
        const cognitoToken = localStorage.getItem('cognito_id_token');
        
        // Check and clear expired tokens
        if (impersonationToken && isTokenExpired(impersonationToken)) {
            console.warn('Impersonation token expired, clearing');
            localStorage.removeItem('impersonation_token');
            localStorage.removeItem('impersonated_user');
        } else if (impersonationToken && impersonatedUserEmail) {
            setIsImpersonating(true);
            setImpersonatedUser(impersonatedUserEmail);
            // Refresh activities for the impersonated user on page load
            loginService.initSession({
                "email": impersonatedUserEmail
            }, () => {
                console.log('Activities refreshed for impersonated user on page load:', impersonatedUserEmail);
            }, (error: any) => {
                console.error('Failed to refresh activities for impersonated user on page load:', error);
            });
        }
        
        // Handle Cognito token - clear if expired, refresh if expiring soon
        if (cognitoToken) {
            if (isTokenExpired(cognitoToken)) {
                console.warn('AuthProvider: token expired, clearing from storage');
                localStorage.removeItem('cognito_id_token');
            } else if (shouldRefreshCognitoToken(cognitoToken)) {
                //console.log('AuthProvider: Cognito token needs refresh, refreshing proactively');
                refreshCognitoTokenInProvider();
            }
        }
    }, []);

    const login = (email: string, callback: VoidFunction, onFailure: (any:any) => {}) => {
        //console.log("Logging in ", email)
        return loginService.initSession({
            "email": email
        }, (userType: string) => {
            setAuthenticated(true);
            setUserType(userType);
            callback();
        }, onFailure);
    };

    let logout = (callback: VoidFunction) => {
        setAuthenticated(false);
        envProvider.user = undefined;
        // Clear all tokens on logout
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('impersonated_user');
        localStorage.removeItem('cognito_id_token');
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setUserType(null);
        callback();
    };

    const startImpersonation = (impersonationToken: string, targetEmail: string) => {
        localStorage.setItem('impersonation_token', impersonationToken);
        localStorage.setItem('impersonated_user', targetEmail);
        setIsImpersonating(true);
        setImpersonatedUser(targetEmail);
        
        // Refresh activities for the impersonated user
        loginService.initSession({
            "email": targetEmail
        }, () => {
            console.log('Activities refreshed for impersonated user:', targetEmail);
        }, (error: any) => {
            console.error('Failed to refresh activities for impersonated user:', error);
        });
    };

    const stopImpersonation = () => {
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('impersonated_user');
        setIsImpersonating(false);
        setImpersonatedUser(null);
        
        // Refresh activities for the original user
        if (envProvider.user?.email) {
            loginService.initSession({
                "email": envProvider.user.email
            }, () => {
                console.log('Activities refreshed for original user:', envProvider.user?.email);
            }, (error: any) => {
                console.error('Failed to refresh activities for original user:', error);
            });
        }
    };

    let value = {authenticated, isImpersonating, impersonatedUser, login, logout, startImpersonation, stopImpersonation, userType};

    // @ts-ignore
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}